import { useContext, useEffect, useRef, useState } from "react";
import { Button } from "../core/button";
import { SalesContext } from "../../SalesContext";
import Notification from "../core/notification";
import moment from "moment";
import { sleep } from "../../utils";
import { BasicModalProps, Modal } from "../core/modal";
import { uploadQcRecording } from "../order/service";

type Props = BasicModalProps;

export const QCModal: React.FC<Props> = props => {
  const [isScriptOpen, setScriptOpen] = useState(false);
  const [nonce, setNonce] = useState(0);
  const context = useContext(SalesContext);

  const [recordingStartAt, setRecordingStartAt] = useState<Date>(null);

  const visualizerContext = useRef<AudioContext>(null);
  const recorder = useRef<MediaRecorder>(null);
  const recordingChunks = useRef<Blob[]>([]);
  const visualizer = useRef<HTMLDivElement>(null);

  //  See https://github.com/mdn/dom-examples/blob/main/media/web-dictaphone/scripts/app.js
  async function createVisualizer(stream: MediaStream) {
    visualizerContext.current = new AudioContext();

    const source = visualizerContext.current.createMediaStreamSource(stream);

    const analyzer = visualizerContext.current.createAnalyser();
    analyzer.fftSize = 32;

    source.connect(analyzer);

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    function updateVisualizer() {
      if (!visualizer.current) {
        requestAnimationFrame(updateVisualizer);
        return;
      }

      analyzer.getByteTimeDomainData(dataArray);

      //  https://stackoverflow.com/a/72928710
      let amplitudeSum = 0;
      dataArray.forEach(amplitude => {
        amplitudeSum += amplitude * amplitude;
      });

      const volume = Math.sqrt(amplitudeSum / dataArray.length);
      const volumeFactor = (volume - 127) / 127;

      visualizer.current.style.transform = `scale(${1 + 3 * volumeFactor})`;

      requestAnimationFrame(updateVisualizer);
    }

    updateVisualizer();
  }

  async function startRecording() {
    try {
      context.setQcRecording(null);

      const stream = recorder.current?.stream ??
        await navigator.mediaDevices.getUserMedia({ audio: true });

      //  https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder
      recorder.current = new MediaRecorder(stream);
      recorder.current.start();

      recordingChunks.current = [];
      setRecordingStartAt(new Date());
      //setRecordingUrl(null);

      recorder.current.addEventListener('dataavailable', event => {
        recordingChunks.current.push(event.data);
      });

      setNonce(Date.now());
      createVisualizer(stream);

    } catch (error) {
      console.error(error);
      Notification.show("Kunde inte starta kvalitetskontroll. Använd röstmemo.");
    }
  }

  async function cancelRecording() {
    try {
      context.setQcRecording(null);

      recorder.current.stop();
      setRecordingStartAt(null);

      setNonce(Date.now());
      //setRecordingUrl(null);

    } catch (error) {
      console.error(error);
      Notification.show("Kunde inte stoppa kvalitetskontroll");
    }
  }

  async function saveRecording() {
    try {
      recorder.current.stop();
      setRecordingStartAt(null);

      //  Wait for dataavailable event.
      for (let i = 0; i < 20; i++) {
        if (recordingChunks.current.find(chunk => {
          if (!(chunk instanceof Blob)) {
            //  ??
            return true;
          }

          return chunk.size > 0;
        })) {
          break;
        }

        await sleep(100);
      }

      const blob = new Blob(recordingChunks.current, { type: "audio/webm; codecs=opus" });

      try {
        await uploadQcRecording(context.agreement.key, new File([blob], "Kvalitetskontroll.ogg"));
      } catch (error) {
        console.error("alert: Error pre-uploading agreement %s QC", context.agreement?.key, error);
      }

      context.setQcRecording(blob);
      setNonce(Date.now());
      //setRecordingUrl(URL.createObjectURL(blob));

    } catch (error) {
      console.error(error);
      Notification.show("Kunde inte spara kvalitetskontroll. Använd röstmemo.");
    } finally {
      props.onClose();
    }
  }

  useEffect(() => {
    if (recordingStartAt) {
      const id = setInterval(() => {
        setNonce(Date.now());
      }, 1000);

      return () => { clearInterval(id); }
    }
  }, [Boolean(recordingStartAt)]);

  return (
    <Modal {...props} onClose={context.qcRecording ? props.onClose : null  /* Förhindra att råka stänga */}>
      <div style={{ minHeight: 'unset', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {
          window.navigator.mediaDevices && recorder.current?.state === "recording" ? (
            <>
              <div>
                <div
                  style={{ display: 'inline-block', borderRadius: '50%', width: 8, height: 8, background: 'red' }}
                  ref={element => visualizer.current = element}></div>&nbsp;
                Spelar in ({moment.utc().subtract(recordingStartAt?.getTime() ?? Date.now(), 'milliseconds').format('HH:mm:ss')})
              </div>

              <Button onClick={() => startRecording()}>
                Starta om inspelning
              </Button>
            </>
          ) : window.navigator.mediaDevices ? (
            <Button weight="primary" onClick={() => startRecording()}>
              Starta kvalitetskontroll
            </Button>

          ) : (
            <div>
              Uppdatera operativsystemet för kvalitetskontroll!
            </div>
          )
        }

        <div
          style={{
            background: '#fff',
            border: '1px solid #eee',
            borderRadius: 6,
            height: 360,
            width: 800,
            overflow: 'auto',
            fontSize: 16,
            textAlign: "center"
          }}>
          <div>Stämmer det att du heter {context.creditCheckResult?.firstName || '<förnamn>'} {context.creditCheckResult?.lastName || '<efternamn>'}?</div><br />

          <div style={{ color: 'blue' }}>{'(var tyst och vänta på svar)'}</div><br />

          <div>Stämmer det att ditt personnummmer är {context.creditCheckResult?.ssn || '<personnummer>'}?</div><br />

          <div style={{ color: 'blue' }}>{'(var tyst och vänta på svar)'}</div><br />

          <div>Hur mycket ska du betala i månaden?</div><br />

          <div style={{ color: 'blue' }}>{'(var tyst och vänta på svar)'}</div><br />

          <div>Hur lång är avtalstiden?</div><br />

          <div style={{ color: 'blue' }}>{'(var tyst och vänta på svar)'}</div><br />

          <div>Och vad heter företaget som kommer skicka fakturorna?</div><br />

          <div style={{ color: 'blue' }}>{'(var tyst och vänta på svar)'}</div><br />
        </div>

        {
          window.navigator.mediaDevices && recorder.current?.state === "recording" ? (
            <Button weight="primary" onClick={() => saveRecording()}>
              Spara kvalitetskontroll
            </Button>
          ) : null
        }
      </div>
    </Modal>
  )
}